import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { get, reduce, pipe, find, size, difference, isEmpty, map, xor, nth } from "lodash/fp";
import { Slider, Rail, Handles } from "react-compound-slider";
import "@reach/auto-id";
import { useEventListener } from "../../hooks/use-event-listener.es.js";
import { useCachedValue } from "../../hooks/use-cached-value.es.js";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { generateSteps } from "../../utils/continuous-slider-mappings.es.js";
import { MultiSliderHandle } from "../multi-slider/multi-slider-handle.es.js";
import { MultiSliderWrapper, MultiSliderImpl, MultiSliderRail } from "../multi-slider/multi-slider.styles.es.js";
const propTypes = {
  disabled: PropTypes.bool,
  enableTooltips: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  showDebug: PropTypes.bool,
  singleValueMode: PropTypes.bool,
  valueLabels: PropTypes.arrayOf(PropTypes.string),
  values: PropTypes.arrayOf(PropTypes.number).isRequired
};
const defaultProps = {
  disabled: false,
  enableTooltips: false,
  showDebug: false,
  singleValueMode: false,
  valueLabels: []
};
const ContinuousSlider = ({
  disabled,
  enableTooltips,
  max,
  min,
  onChange,
  showDebug,
  singleValueMode,
  valueLabels,
  values
}) => {
  const sliderWrapperRef = useRef(null);
  const [sliderWidth, setSliderWidth] = useState();
  const getWidth = useCallback(() => {
    const width = get(["current", "offsetWidth"], sliderWrapperRef);
    setSliderWidth(width);
  }, [sliderWrapperRef]);
  useEffect(getWidth, [getWidth]);
  useEventListener("resize", getWidth);
  const handlePointerDown = useCallback((event) => {
    const { currentTarget, pointerId } = event;
    if (currentTarget.setPointerCapture) {
      currentTarget.setPointerCapture(pointerId);
    }
  }, []);
  const handlePointerUp = useCallback((event) => {
    const { currentTarget, pointerId } = event;
    if (currentTarget.releasePointerCapture) {
      currentTarget.releasePointerCapture(pointerId);
    }
  }, []);
  const [isSliding, setSliding] = useState(false);
  const handleSlideStart = useCallback(() => {
    setSliding(true);
  }, []);
  const handleSlideEnd = useCallback(() => {
    setSliding(false);
  }, []);
  const domain = useMemo(() => [0, sliderWidth], [sliderWidth]);
  const valueSteps = useMemo(
    () => generateSteps({ sliderWidth, min, max }),
    [max, min, sliderWidth]
  );
  const handleToValue = useCallback(
    (handleValue) => reduce(
      (value, [stepValue, segmentLeft]) => {
        if (value)
          return value;
        if (segmentLeft <= handleValue) {
          return stepValue;
        }
        return value;
      },
      void 0,
      valueSteps
    ),
    [valueSteps]
  );
  const valueToHandle = useCallback(
    (value) => pipe(
      find(([stepValue]) => value === stepValue),
      get(2)
    )(valueSteps),
    [valueSteps]
  );
  const [handleValues, setHandleValues] = useState([]);
  const cachedHandleValues = useCachedValue(handleValues, isSliding);
  const cachedValues = useCachedValue(values, isSliding);
  const onScrub = useCallback(
    (newSliderValues) => {
      let sliderValues = newSliderValues;
      if (singleValueMode && size(newSliderValues) > 1) {
        const diff = difference(newSliderValues, cachedHandleValues);
        if (!isEmpty(diff))
          sliderValues = diff;
      }
      setHandleValues(sliderValues);
      const newValues = map(handleToValue, sliderValues);
      if (!isEmpty(xor(newValues, values))) {
        onChange(newValues);
      }
    },
    [cachedHandleValues, onChange, handleToValue, singleValueMode, values]
  );
  useEffect(() => {
    if (sliderWidth && !isEmpty(xor(map(handleToValue, cachedHandleValues), cachedValues))) {
      setHandleValues(map(valueToHandle, cachedValues));
    }
  }, [
    cachedHandleValues,
    cachedValues,
    handleToValue,
    sliderWidth,
    valueToHandle
  ]);
  const onSingleSelect = useCallback(
    (value) => {
      if (singleValueMode && onChange) {
        onChange(value);
      }
    },
    [onChange, singleValueMode]
  );
  return /* @__PURE__ */ jsxs(MultiSliderWrapper, { ref: sliderWrapperRef, children: [
    !isEmpty(valueSteps) && /* @__PURE__ */ jsxs(
      Slider,
      {
        component: MultiSliderImpl,
        disabled,
        domain,
        onSlideEnd: handleSlideEnd,
        onSlideStart: handleSlideStart,
        onUpdate: onScrub,
        rootProps: {
          onPointerDown: handlePointerDown,
          onPointerUp: handlePointerUp
        },
        step: 1,
        values: cachedHandleValues,
        children: [
          /* @__PURE__ */ jsx(Rail, { children: ({ getRailProps }) => /* @__PURE__ */ jsx(MultiSliderRail, { ...getRailProps() }) }),
          /* @__PURE__ */ jsx(Handles, { children: ({ handles, activeHandleID, getHandleProps } = {}) => /* @__PURE__ */ jsx(Fragment, { children: handles.map((handle, i) => /* @__PURE__ */ jsx(
            MultiSliderHandle,
            {
              active: handle.id === activeHandleID,
              "data-handle-id": handle.id,
              disabled,
              enableTooltip: enableTooltips,
              isMarker: singleValueMode && size(handles) > 1,
              max: sliderWidth,
              min: 0,
              onSelect: onSingleSelect,
              percent: handle.percent,
              value: handleToValue(handle.value),
              valueLabel: nth(i, valueLabels),
              ...getHandleProps(handle.id)
            },
            handle.id
          )) }) })
        ]
      }
    ),
    showDebug && /* @__PURE__ */ jsx("pre", { children: JSON.stringify(handleValues) })
  ] });
};
ContinuousSlider.propTypes = propTypes;
ContinuousSlider.defaultProps = defaultProps;
export {
  ContinuousSlider
};
